/**
 * Linking Service - Rotation-to-Timecard linking API calls
 * Handles matching rotations to timecard entries
 */
import apiClient, { handleApiError } from './apiConfig';

console.log('[linkingService] Module loaded');

/**
 * Get links for a specific bid period
 * @param {string} bidPeriod - The bid period (e.g., "2025-09")
 * @returns {Promise<Array>} Array of links for the period
 */
export const getLinksForPeriod = async (bidPeriod) => {
  console.log('[linkingService] getLinksForPeriod called with:', bidPeriod);
  try {
    const response = await apiClient.get(`/linking/period/${bidPeriod}`);
    console.log('[linkingService] getLinksForPeriod response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[linkingService] getLinksForPeriod error:', error);
    throw new Error(handleApiError(error, 'Failed to get links for period'));
  }
};

/**
 * Get linking status
 * @returns {Promise<Object>} Current linking status
 */
export const getLinkingStatus = async () => {
  try {
    const response = await apiClient.get('/linking/status');
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error, 'Failed to get linking status'));
  }
};

/**
 * Get link suggestions with confidence scores
 * @param {Object} params - Query parameters
 * @param {number} params.minimum_score - Minimum confidence (default 0.3)
 * @param {number} params.rotation_id - Filter by rotation (optional)
 * @param {number} params.timecard_id - Filter by timecard (optional)
 * @returns {Promise<Object>} Suggestions categorized by confidence
 */
export const getLinkSuggestions = async ({ minimum_score = 0.3, rotation_id = null, timecard_id = null } = {}) => {
  console.log('[linkingService] getLinkSuggestions called with:', { minimum_score, rotation_id, timecard_id });
  try {
    const params = { minimum_score };
    if (rotation_id) params.rotation_id = rotation_id;
    if (timecard_id) params.timecard_id = timecard_id;

    const response = await apiClient.get('/linking/suggestions', { params });
    console.log('[linkingService] getLinkSuggestions response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[linkingService] getLinkSuggestions error:', error);
    throw new Error(handleApiError(error, 'Failed to get link suggestions'));
  }
};

/**
 * Create a manual link between rotation and timecard
 * @param {Object} linkData - Link data
 * @param {number} linkData.rotation_id - Rotation ID
 * @param {number} linkData.timecard_id - Timecard ID
 * @param {string} linkData.notes - Optional notes
 * @returns {Promise<Object>} Created link
 */
export const createLink = async ({ rotation_id, timecard_id, notes = null }) => {
  try {
    const response = await apiClient.post('/linking/manual', {
      rotation_id,
      timecard_id,
      notes
    });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error, 'Failed to create link'));
  }
};

/**
 * Delete an existing link
 * @param {number} linkId - The link ID to delete
 * @returns {Promise<void>}
 */
export const deleteLink = async (linkId) => {
  try {
    await apiClient.delete(`/linking/${linkId}`);
  } catch (error) {
    throw new Error(handleApiError(error, 'Failed to delete link'));
  }
};

/**
 * Auto-link rotations to timecards for a specific period
 * @param {string} bidPeriod - The bid period (e.g., "2025-09")
 * @param {Object} params - Auto-link parameters
 * @param {number} params.minimum_score - Minimum confidence (0.0-1.0, default 0.6)
 * @param {boolean} params.dry_run - If true, don't create links, just show suggestions
 * @returns {Promise<Object>} Result with created links and statistics
 */
export const autoLinkPeriod = async (bidPeriod, { minimum_score = 0.6, dry_run = false } = {}) => {
  console.log('[linkingService] autoLinkPeriod called with:', { bidPeriod, minimum_score, dry_run });
  try {
    const response = await apiClient.post(`/linking/auto-link/${bidPeriod}`, {
      minimum_score,
      dry_run
    });
    console.log('[linkingService] autoLinkPeriod response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[linkingService] autoLinkPeriod error:', error);
    throw new Error(handleApiError(error, 'Failed to auto-link period'));
  }
};

/**
 * Auto-link rotations to timecards
 * @param {Object} params - Auto-link parameters
 * @param {number} params.minimum_score - Minimum confidence (0.0-1.0, default 0.6)
 * @param {boolean} params.dry_run - If true, don't create links, just show suggestions
 * @returns {Promise<Object>} Result with created links and statistics
 */
export const autoLink = async ({ minimum_score = 0.6, dry_run = false } = {}) => {
  console.log('[linkingService] autoLink called with:', { minimum_score, dry_run });
  try {
    const response = await apiClient.post('/linking/auto-link', {
      minimum_score,
      dry_run
    });
    console.log('[linkingService] autoLink response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[linkingService] autoLink error:', error);
    throw new Error(handleApiError(error, 'Failed to auto-link'));
  }
};

/**
 * Get unlinked rotations in a period
 * @param {number} periodId - The period ID
 * @returns {Promise<Array>} Array of unlinked rotations
 */
export const getUnlinkedRotations = async (periodId) => {
  try {
    const response = await apiClient.get(`/linking/unlinked/rotations/${periodId}`);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error, 'Failed to get unlinked rotations'));
  }
};

/**
 * Get unlinked timecard entries in a period
 * @param {number} periodId - The period ID
 * @returns {Promise<Array>} Array of unlinked timecard entries
 */
export const getUnlinkedTimecardEntries = async (periodId) => {
  try {
    const response = await apiClient.get(`/linking/unlinked/timecard/${periodId}`);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error, 'Failed to get unlinked timecard entries'));
  }
};

/**
 * Bulk update links
 * @param {Array} links - Array of link updates
 * @returns {Promise<Object>} Bulk update result
 */
export const bulkUpdateLinks = async (links) => {
  try {
    const response = await apiClient.post('/linking/bulk', { links });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error, 'Failed to bulk update links'));
  }
};

export default {
  getLinksForPeriod,
  getLinkSuggestions,
  createLink,
  deleteLink,
  autoLinkPeriod,
  getUnlinkedRotations,
  getUnlinkedTimecardEntries,
  bulkUpdateLinks,
};
